// client/src/services/lexistarAssets.js
//
// LexiStar asset register + D26 asset ownership API client (ST-00188, T1-S08 /
// T3-S01). Server: server/modules/lexistar/fleet (/api/lexistar/assets,
// /api/lexistar/asset-ownership).
//
// Thin wrapper over the shared axios instance in `services/api` so the page
// inherits the cookie-session auth interceptors, CSRF header and single-flight
// 401 refresh. `api` already carries `/api` in its baseURL. Every helper
// resolves to the response BODY. Asset and alias payloads use the DB column
// names (snake_case); the transfer payload is { assetId, toTenantId, effectiveFrom }.

import api from './api';

const ASSETS = '/lexistar/assets';
const OWNERSHIP = '/lexistar/asset-ownership';

/** Drop undefined / null / '' so the API never sees `q=` or `tenantId=`. */
const clean = (params = {}) =>
  Object.fromEntries(
    Object.entries(params).filter(([, v]) => v !== undefined && v !== null && v !== '')
  );

const body = (res) => res.data;

/**
 * Visible assets, paged: { assets, pagination: { page, limit, total, totalPages },
 * summary: { byType } }. Each asset carries access_type ('Transporter' = your
 * organisation owns it; 'Admin' = global-admin view of another tenant's asset).
 * @param {{q?:string,page?:number,limit?:number,tenantId?:number}} params
 */
export const listAssets = (params) => api.get(ASSETS, { params: clean(params) }).then(body);

/** { asset, aliases, ownership } */
export const getAsset = (id) => api.get(`${ASSETS}/${id}`).then(body);

/** { asset } — 409 { error, existingAssetId } on a duplicate registration. */
export const createAsset = (data) => api.post(ASSETS, data).then(body);

/** { asset } — send `version` for optimistic concurrency (409 when stale). */
export const updateAsset = (id, data) => api.patch(`${ASSETS}/${id}`, data).then(body);

/** Soft delete: { deleted: { id, registration, aliasesRemoved } } */
export const deleteAsset = (id) => api.delete(`${ASSETS}/${id}`).then(body);

/** { alias } */
export const addAlias = (id, data) => api.post(`${ASSETS}/${id}/aliases`, data).then(body);

export const removeAlias = (id, aliasId) =>
  api.delete(`${ASSETS}/${id}/aliases/${aliasId}`).then(body);

/**
 * One asset per device-registry registration that has none yet (idempotent):
 * { created, alreadyRegistered, registrations, devicesWithRegistration, assets }.
 */
export const importFromDevices = (data = {}) =>
  api.post(`${ASSETS}/import-from-devices`, clean(data)).then(body);

/** { asset, ownership } — non-admins see only their own organisation's periods. */
export const getOwnershipHistory = (assetId) =>
  api.get(OWNERSHIP, { params: { assetId } }).then(body);

/**
 * Transfer to another organisation. effectiveFrom: 'YYYY-MM-DD' (midnight SAST),
 * omitted = now. Not in the future.
 * @param {{assetId:string,toTenantId:number,effectiveFrom?:string}} data
 */
export const transferOwnership = (data) =>
  api.post(`${OWNERSHIP}/transfer`, clean(data)).then(body);

/** Active organisations for the transfer picker (existing tenants API; global admins only). */
export const listTransferTenants = () =>
  api.get('/tenants/current/all').then((res) => res.data?.data || res.data || []);
